import { useState, useRef, useEffect } from "react";

function ChatBubbleIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M20 11.5C20 15.6421 16.4183 19 12 19C10.8436 19 9.74457 18.77 8.75294 18.3578L5 19.5L6.19608 16.2402C4.82765 14.9647 4 13.3091 4 11.5C4 7.35786 7.58172 4 12 4C16.4183 4 20 7.35786 20 11.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="11.5" r="0.8" fill="currentColor" />
      <circle cx="12" cy="11.5" r="0.8" fill="currentColor" />
      <circle cx="15" cy="11.5" r="0.8" fill="currentColor" />
    </svg>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Welcome to the Studio! ✨ How can we help you create something timeless today?",
    },
  ]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getAutoReply = (message) => {
    const msg = message.toLowerCase();
    if(msg.includes("hello") || msg.includes("hi") || msg.includes("hey"))
      return "Hello there! 👋 How can we assist you today?";
    if(msg.includes("quote")) 
      return "We'd be happy to provide a quote for your project. Please share more details about your vision and requirements.";
    if (msg.includes("price") || msg.includes("cost"))
      return "Our packages are bespoke to your vision. Would you like to speak to a coordinator for a personalized quote?";
    if (msg.includes("wedding"))
      return "Congratulations! 🥂 We specialize in stress-free weddings. Tell us your dream date and location.";
    if (msg.includes("birthday"))
      return "Let's celebrate! 🎈 Share your theme ideas and expected guest count.";
    if (msg.includes("corporate"))
      return "We help brands stand out. Tell us more about your corporate branding or event goals.";
    if (msg.includes("printing") || msg.includes("t-shirt"))
      return "We offer premium custom apparel. Please share your design ideas and quantity.";

    return "Thank you for reaching out! A studio coordinator will be with you shortly.";
  };

  const sendMessage = () => {
    if (!input.trim()) return;

    const userMessage = { sender: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    setTimeout(() => {
      setMessages((prev) => [...prev, { sender: "bot", text: getAutoReply(input) }]);
    }, 800);
  };

  return (
    <div className="fixed bottom-8 right-8 z-[1000] font-sans">
      <button
        onClick={() => setOpen(!open)}
        className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-500 transform hover:scale-110 active:scale-95 ${
          open
            ? "bg-neutral-900 text-white rotate-90"
            : "bg-gradient-to-br from-pink-50 via-white to-teal-50 text-neutral-800 border border-pink-100/70"
        }`}
        aria-label={open ? "Close chat widget" : "Open chat widget"}
      >
        {open ? (
          <span className="text-3xl leading-none translate-y-[-1px]">&times;</span>
        ) : (
          <ChatBubbleIcon className="w-7 h-7" />
        )}
      </button>

      <div
        className={`absolute bottom-20 right-0 w-[340px] md:w-[380px] bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.15)] overflow-hidden transition-all duration-500 origin-bottom-right ${
          open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="p-6 bg-gradient-to-r from-teal-50 to-pink-50 border-b border-neutral-50 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm text-xs border border-pink-100">
            ✨
          </div>
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-800">
              Studio Concierge
            </h3>
            <p className="text-[10px] text-teal-600 font-medium">Online & Ready to Assist</p>
          </div>
        </div>

        <div className="h-[320px] overflow-y-auto p-6 flex flex-col gap-4 scrollbar-hide">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`max-w-[80%] px-4 py-3 rounded-[1.2rem] text-[13px] leading-relaxed transition-all duration-300 ${
                msg.sender === "user"
                  ? "bg-neutral-900 text-white self-end rounded-tr-none"
                  : "bg-neutral-50 text-neutral-700 self-start rounded-tl-none border border-neutral-100"
              }`}
            >
              {msg.text}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 bg-white border-t border-neutral-50 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Write a message..."
            className="flex-1 bg-neutral-50 border-none rounded-full px-5 py-3 text-[13px] focus:ring-2 focus:ring-pink-100 outline-none transition-all"
          />
          <button
            onClick={sendMessage}
            className="w-10 h-10 rounded-full bg-neutral-900 text-white flex items-center justify-center hover:bg-neutral-700 transition-colors shadow-lg active:scale-90"
          >
            <span className="text-xs translate-x-[1px]">➤</span>
          </button>
        </div>
      </div>
    </div>
  );
}
